import React from "react";
import {Alert, LinearProgress} from "@mui/material";
import {AddTaskOutlined, CheckCircleOutline, RocketLaunchOutlined, SavingsOutlined} from "@mui/icons-material";
import {AccordionLight} from "~/components/_tiny/AccordionLight";
import {Markdown} from "~/pages/contract-explorer/components/Markdown.tsx";
import type {SitKpiPill} from "~/sit/components/SitKpiHeader/SitKpiHeader.tsx";
import {usePtV2Context} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Context.ts";
import type {
    PtV2Action,
    PtV2InitiativeSource,
    PtV2PlanItem,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {
    BASELINE_DAYS,
    buildPlanGroups,
    harmonizationActionInput,
    harmonizationView,
    leakageActionInput,
    userAddedActions,
} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {formatEur, formatInt} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {
    usePtV2ActionsQuery,
    usePtV2DeleteActionMutation,
    usePtV2HarmonizationQuery,
    usePtV2LeakageQuery,
    usePtV2PlaybookQuery,
    usePtV2SetActionStatusMutation,
} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2TabFrame} from "~/experiments/pages/payment-terms-v2/components/PtV2TabFrame.tsx";
import {PtV2Card} from "~/experiments/pages/payment-terms-v2/components/PtV2Card.tsx";
import {PtV2ActionItem} from "~/experiments/pages/payment-terms-v2/components/PtV2ActionItem.tsx";
import {PtV2InitiativeControl} from "~/experiments/pages/payment-terms-v2/components/PtV2InitiativeControl.tsx";

export const ActionPlanTab: React.FC = () => {
    const {hasData} = usePtV2Context();
    const leakageQ = usePtV2LeakageQuery(hasData);
    const harmQ = usePtV2HarmonizationQuery(BASELINE_DAYS, hasData);
    const actionsQ = usePtV2ActionsQuery(hasData);
    const playbookQ = usePtV2PlaybookQuery(hasData);
    const statusM = usePtV2SetActionStatusMutation();
    const deleteM = usePtV2DeleteActionMutation();

    const actions = React.useMemo(() => actionsQ.data ?? [], [actionsQ.data]);
    const byKey = React.useMemo(() => new Map(actions.map(a => [a.key, a])), [actions]);
    const groups = React.useMemo(
        () => buildPlanGroups(leakageQ.data ?? [], harmQ.data?.rows ?? []),
        [leakageQ.data, harmQ.data],
    );
    // Supplier details for items added from a drawer, so their initiative is pre-filled like a recommended one.
    const sources = React.useMemo(() => {
        const m = new Map<string, PtV2InitiativeSource | null | undefined>();
        for (const r of leakageQ.data ?? []) {
            const i = leakageActionInput(r);
            m.set(i.key, i.source);
        }
        // Drawer items default to the workpaper's per-supplier target.
        for (const r of harmonizationView(harmQ.data?.rows ?? [], 'supplier')) {
            if (!r.targetCode) continue;
            const i = harmonizationActionInput(r, r.targetCode);
            m.set(i.key, i.source);
        }
        return m;
    }, [leakageQ.data, harmQ.data]);
    // Newest first, so an item just turned into an initiative from a drawer is at the top.
    const mine = userAddedActions(actions, groups);
    const isDone = (key: string) => byKey.get(key)?.status === 'done';

    const recommendedKeys = groups.flatMap(g => g.items.map(i => i.key));
    const total = recommendedKeys.length + mine.length;
    const done = recommendedKeys.filter(isDone).length + mine.filter(a => a.status === 'done').length;
    const initiatives = actions.filter(a => a.initiativeId != null).length;
    const quickValue = groups[0].items.reduce((s, i) => s + (i.value ?? 0), 0);
    const busy = statusM.isPending || deleteM.isPending;
    const playbook = playbookQ.data?.markdown?.trim();

    // A recommended item only reaches the backend once it is touched; after that, checking it changes only its status.
    const toggle = (item: PtV2PlanItem | PtV2Action) => {
        const status = isDone(item.key) ? 'open' : 'done';
        void statusM.setStatus(item, byKey.get(item.key), status).catch(() => undefined);
    };
    const initiative = (item: PtV2PlanItem) => <PtV2InitiativeControl item={item} existing={byKey.get(item.key)}/>;

    const kpis: SitKpiPill[] = [
        {tone: 'ok', icon: <CheckCircleOutline/>, label: 'Done', value: `${formatInt(done)} of ${formatInt(total)}`},
        {tone: 'info', icon: <SavingsOutlined/>, label: 'Quick win value', value: formatEur(quickValue)},
        {tone: 'plain', icon: <RocketLaunchOutlined/>, label: 'Savings initiatives', value: formatInt(initiatives)},
    ];

    return <PtV2TabFrame
        tab="actions"
        kpis={kpis}
        loading={leakageQ.isPending || harmQ.isPending || actionsQ.isPending}
        error={leakageQ.isError || harmQ.isError || actionsQ.isError}
        summary="The recommended next steps from the analysis, plus anything added from a supplier’s detail panel. Create a savings initiative to track an item in the Savings Initiative Tracker.">
        <div className="ptv2-section">
            <div className="ptv2-progress">
                <LinearProgress variant="determinate" color="success" value={total ? done / total * 100 : 0}
                                aria-label="Action plan progress"/>
                <span className="ptv2-num ptv2-muted">{total ? Math.round(done / total * 100) : 0}%</span>
            </div>
            {(statusM.isError || deleteM.isError) && <Alert severity="error">
                The action plan could not be saved. Try again.
            </Alert>}
            {playbook && <PtV2Card>
                <AccordionLight summary={<b>Playbook{playbookQ.data?.version != null ? ` v${playbookQ.data.version}` : ''}</b>}>
                    <div className="ptv2-playbook"><Markdown text={playbook}/></div>
                </AccordionLight>
            </PtV2Card>}
            <PtV2Card title="Added by you">
                {mine.length === 0
                    ? <div className="ptv2-empty is-compact">
                        <span className="ptv2-empty-icon"><AddTaskOutlined/></span>
                        <p className="ptv2-empty-title">Nothing added yet</p>
                        <p className="ptv2-empty-text">
                            Open a supplier in Payment term leakage or Term harmonization and choose Create savings initiative.
                        </p>
                    </div>
                    // An item linked to an initiative cannot be removed, so the link is not lost.
                    : mine.map(a => <PtV2ActionItem
                        key={a.id} title={a.title} subtitle={a.subtitle} value={a.value}
                        done={a.status === 'done'} busy={busy}
                        onToggle={() => toggle(a)}
                        initiative={initiative({...a, source: sources.get(a.key)})}
                        onRemove={a.initiativeId == null ? () => void deleteM.remove(a.id).catch(() => undefined) : undefined}/>)}
            </PtV2Card>
            {groups.map(g => {
                const groupValue = g.items.reduce((s, i) => s + (i.value ?? 0), 0);
                return <PtV2Card key={g.group} title={g.title}
                                 right={groupValue > 0 && <b className="ptv2-good ptv2-num">{formatEur(groupValue)}</b>}>
                    <p className="ptv2-muted ptv2-card-sub">{g.subtitle}</p>
                    {g.items.length === 0 && <p className="ptv2-muted">Nothing in this data qualifies.</p>}
                    {g.items.map(i => <PtV2ActionItem
                        key={i.key} title={i.title} value={i.value} done={isDone(i.key)} busy={busy}
                        onToggle={() => toggle(i)}
                        initiative={initiative(i)}/>)}
                </PtV2Card>;
            })}
        </div>
    </PtV2TabFrame>;
};
